import { useCallback, useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import ChartTooltip from "./ChartTooltip.jsx";
import { useElementWidth } from "../hooks/useElementWidth.js";
import styles from "./TrendChart.module.css";

export default function TrendChart({ data }) {
  const svgRef = useRef(null);
  const [measureRef, width] = useElementWidth();
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // The same node is both the d3 target and the measured one.
  const setSvg = useCallback((node) => {
    svgRef.current = node;
    measureRef(node);
  }, [measureRef]);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    const el = svgRef.current;
    const height = 200;
    const margin = { top: 16, right: 8, bottom: 28, left: 32 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    // A hidden panel measures 0 and would draw negative geometry. Leave the
    // last good render alone; the observer redraws once it is on screen.
    if (innerW <= 0) return;

    d3.select(el).selectAll("*").remove();

    const svg = d3.select(el)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", height);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scalePoint()
      .domain(data.map((d) => d.date))
      .range([0, innerW])
      .padding(0.2);

    const maxCount = d3.max(data, (d) => d.workoutCount) || 1;
    const y = d3.scaleLinear()
      .domain([0, Math.ceil(maxCount * 1.2)])
      .range([innerH, 0])
      .nice();

    // Grid lines
    g.append("g")
      .selectAll("line")
      .data(y.ticks(4))
      .join("line")
      .attr("x1", 0)
      .attr("x2", innerW)
      .attr("y1", (d) => y(d))
      .attr("y2", (d) => y(d))
      .attr("stroke", "rgba(38,33,25,0.08)")
      .attr("stroke-width", 1);

    // Y axis labels
    g.append("g")
      .selectAll("text")
      .data(y.ticks(4))
      .join("text")
      .attr("x", -8)
      .attr("y", (d) => y(d) + 4)
      .attr("text-anchor", "end")
      .attr("fill", "var(--muted)")
      .attr("font-family", "var(--font-sans)")
      .attr("font-size", 11)
      .text((d) => d);

    // Area fill
    const area = d3.area()
      .x((d) => x(d.date))
      .y0(innerH)
      .y1((d) => y(d.workoutCount))
      .curve(d3.curveCatmullRom.alpha(0.5));

    g.append("path")
      .datum(data)
      .attr("fill", "rgba(217,126,36,0.14)")
      .attr("d", area)
      .attr("fill-opacity", 0)
      .transition()
      .duration(700)
      .ease(d3.easeCubicOut)
      .attr("fill-opacity", 1);

    // Line
    const line = d3.line()
      .x((d) => x(d.date))
      .y((d) => y(d.workoutCount))
      .curve(d3.curveCatmullRom.alpha(0.5));

    const linePath = g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "var(--amber)")
      .attr("stroke-width", 2.5)
      .attr("stroke-linecap", "round")
      .attr("d", line);

    const lineLen = linePath.node().getTotalLength();
    linePath
      .attr("stroke-dasharray", lineLen)
      .attr("stroke-dashoffset", lineLen)
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attr("stroke-dashoffset", 0);

    // Dots — skip when there are too many points to avoid overlap
    if (data.length <= 26) {
      g.selectAll("circle.dot")
        .data(data)
        .join("circle")
        .attr("class", "dot")
        .attr("cx", (d) => x(d.date))
        .attr("cy", (d) => y(d.workoutCount))
        .attr("r", 0)
        .attr("fill", "var(--canvas)")
        .attr("stroke", "var(--amber)")
        .attr("stroke-width", 2.5)
        .transition()
        .delay(700)
        .duration(250)
        .ease(d3.easeBackOut)
        .attr("r", 4);
    }

    // X axis labels
    const step = Math.ceil(data.length / 6);
    const labelData = data.filter((_, i) => i === 0 || i === data.length - 1 || i % step === 0);

    g.selectAll(".xlabel")
      .data(labelData)
      .join("text")
      .attr("class", "xlabel")
      .attr("x", (d) => x(d.date))
      .attr("y", innerH + 18)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--muted)")
      .attr("font-family", "var(--font-sans)")
      .attr("font-size", 11)
      .text((d) => d.label);

    // Hover crosshair
    const crosshair = g.append("line")
      .attr("y1", 0).attr("y2", innerH)
      .attr("stroke", "rgba(38,33,25,0.30)")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,3")
      .attr("opacity", 0)
      .attr("pointer-events", "none");

    const hoverDot = g.append("circle")
      .attr("r", 5)
      .attr("fill", "var(--amber)")
      .attr("stroke", "var(--canvas)")
      .attr("stroke-width", 2)
      .attr("opacity", 0)
      .attr("pointer-events", "none");

    // Transparent overlay rect captures mouse
    g.append("rect")
      .attr("width", innerW)
      .attr("height", innerH)
      .attr("fill", "transparent")
      .attr("cursor", "crosshair")
      .on("mousemove", function(event) {
        const [mx] = d3.pointer(event);
        const xPositions = data.map((d) => x(d.date));
        const idx = d3.minIndex(xPositions, (v) => Math.abs(v - mx));
        const d = data[idx];
        if (!d) return;

        crosshair.attr("x1", x(d.date)).attr("x2", x(d.date)).attr("opacity", 1);
        hoverDot.attr("cx", x(d.date)).attr("cy", y(d.workoutCount)).attr("opacity", 1);

        setTooltip({ label: d.label, count: d.workoutCount });
        setTooltipPos({ x: event.clientX, y: event.clientY });
      })
      .on("mouseleave", function() {
        crosshair.attr("opacity", 0);
        hoverDot.attr("opacity", 0);
        setTooltip(null);
      });
  }, [data, width]);

  return (
    <div id="weekly-trend-chart" className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.label}>Weekly workouts</span>
      </div>
      {data.length === 0 ? (
        <div className={styles.empty}>No data for this period</div>
      ) : (
        <svg ref={setSvg} className={styles.svg} />
      )}
      {tooltip && (
        <ChartTooltip className={styles.tooltip} x={tooltipPos.x} y={tooltipPos.y}>
          <span className={styles.tooltipDate}>{tooltip.label}</span>
          <span className={styles.tooltipValue}>{tooltip.count} workout{tooltip.count !== 1 ? "s" : ""}</span>
        </ChartTooltip>
      )}
    </div>
  );
}
